import {
  CompilationSortOption,
  Content,
  ContentPreview,
} from "@turtlewave/types";

import { apiConfig } from "../utils/client";

import { ContentFetchError } from "./errors";
import { validateContentArray, validateContentData } from "./schema";

export interface ContentListOptions {
  compilationId?: string;
  sort?: CompilationSortOption;
}

/**
 * ContentService - Singleton service for content data access
 */
class ContentService {
  private static instance: ContentService;

  private constructor() {}

  static getInstance(): ContentService {
    if (!ContentService.instance) {
      ContentService.instance = new ContentService();
    }
    return ContentService.instance;
  }

  /**
   * Fetch a single content item by slug or ID
   * @param idOrSlug - Content slug or ID
   * @returns Promise resolving to full Content object
   * @throws ContentFetchError - On fetch failure
   * @throws ContentValidationError - On validation failure
   */
  async getContentByIdOrSlug(
    idOrSlug: string,
    init?: { signal?: AbortSignal },
  ): Promise<Content> {
    try {
      const response = await fetch(apiConfig.content.byIdOrSlug(idOrSlug), {
        signal: init?.signal,
      });

      if (!response.ok) {
        throw new ContentFetchError(
          `Failed to fetch content: ${response.status} ${response.statusText}`,
          undefined,
          response.status,
        );
      }

      const rawData = await response.json();
      return validateContentData(rawData.content);
    } catch (error) {
      if (error instanceof ContentFetchError) throw error;
      if (error instanceof Error && error.name === "ContentValidationError") {
        throw error;
      }
      throw new ContentFetchError(
        `Failed to fetch content: ${idOrSlug}`,
        error,
      );
    }
  }

  /**
   * Fetch content previews, optionally filtered by compilation
   * @param options - Optional filtering and sorting params
   * @returns Promise resolving to array of ContentPreview
   * @throws ContentFetchError - On fetch failure
   * @throws ContentValidationError - On validation failure
   */
  async getAllContentPreviews(
    options?: ContentListOptions,
    init?: { signal?: AbortSignal },
  ): Promise<ContentPreview[]> {
    try {
      const queryParams: Record<string, string> = { preview: "true" };
      if (options?.compilationId) {
        queryParams.compilation_id = options.compilationId;
      }
      if (options?.sort) {
        queryParams.sort = options.sort;
      }
      const response = await fetch(apiConfig.content.list(queryParams), {
        signal: init?.signal,
      });

      if (!response.ok) {
        throw new ContentFetchError(
          `Failed to fetch content: ${response.status} ${response.statusText}`,
          undefined,
          response.status,
        );
      }

      // Extract raw data
      const rawData = await response.json();
      // Validate with Zod
      return validateContentArray(rawData.content);
    } catch (error) {
      if (error instanceof ContentFetchError) throw error;
      // Re-throw ContentValidationError as-is
      if (error instanceof Error && error.name === "ContentValidationError") {
        throw error;
      }
      // Wrap other errors in ContentFetchError
      throw new ContentFetchError("Failed to fetch content data", error);
    }
  }
}

// Export singleton instance
export const contentService = ContentService.getInstance();
